import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Activity, CheckCircle2, AlertTriangle, RefreshCw, Server, Cable, MonitorCheck,
    LifeBuoy, Trash2, Download, Square, Circle, Wand2, History, ChevronRight,
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { diagnosticApi } from '../api/tenants.js';
import { warningsApi } from '../api/warnings.js';
import { get } from '../api/client.js';
import { superAdminApi } from '../api/superAdmin.js';
import { useTenant } from '../components/TenantContext.jsx';
import { PhoneCall, PhoneIncoming, PhoneOutgoing, Radio } from 'lucide-react';

const REFRESH_MS = 5000;

const SERVICES = [
    { key: 'adminConsole', icon: MonitorCheck },
    { key: 'backend', icon: Server },
    { key: 'sidecar', icon: Cable },
    { key: 'ivrBuilder', icon: Wand2 },
];

export default function SystemHealthPage() {
    const { t } = useTranslation('system');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await fetch('/healthz', { cache: 'no-store' });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const json = await r.json();
            setData(json);
            setError(null);
            setLastFetched(Date.now());
        } catch (e) {
            setError(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    const overall = computeOverall(data, t);

    return (
        <div className="max-w-5xl">
            <PageHeader
                eyebrow="system · status"
                title={t('health.title')}
                description={t('health.description')}
                actions={
                    <Button variant="ghost" onClick={load}>
                        <RefreshCw size={14} /> {t('health.refreshNow')}
                    </Button>
                }
            />

            <OverallBanner overall={overall} lastFetched={lastFetched} loading={loading} error={error} />

            <div className="grid gap-4 mt-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                {SERVICES.map((s) => (
                    <ServiceCard
                        key={s.key}
                        meta={s}
                        state={s.key === 'adminConsole'
                            ? buildAdminConsoleState(data, error)
                            : data?.[s.key]}
                    />
                ))}
            </div>

            <PbxStatusPanel />
            <LiveCallsPanel />
            <RecentFailuresPanel />

            <DiagnosticToolsPanel />
            <VersionPanel />
        </div>
    );
}

// ── 版本與更新記錄（backend /api/sip-relay/version）──────────────────────────
// 超管：匯出區間內的版本更新清單 xlsx（PM 每週要的「M/D–M/D 版本更新紀錄」，預設最近 7 天）
const dayTaipei = (ms) => new Date(ms + 8 * 3600 * 1000).toISOString().slice(0, 10);
function ReleaseNotesExport() {
    const { t } = useTranslation('system');
    const [from, setFrom] = useState(dayTaipei(Date.now() - 6 * 86400 * 1000));
    const [to, setTo] = useState(dayTaipei(Date.now()));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const run = async () => {
        setBusy(true); setErr(null);
        try { await superAdminApi.downloadReleaseNotes(from, to); }
        catch (e) { setErr(e.message || String(e)); }
        finally { setBusy(false); }
    };
    const valid = from && to && from <= to;
    return (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-ink-700">
            <span className="font-medium">{t('version.export.title')}</span>
            <input id="rn-from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
                aria-label={t('version.export.from')} className="rounded border border-cream-300 px-2 py-1 bg-white" />
            <span>～</span>
            <input id="rn-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)}
                aria-label={t('version.export.to')} className="rounded border border-cream-300 px-2 py-1 bg-white" />
            <Button size="sm" variant="outline" onClick={run} disabled={!valid || busy}>
                <Download size={13} /> {busy ? t('version.export.busy') : t('version.export.button')}
            </Button>
            {err && <span className="text-danger-500">{err}</span>}
        </div>
    );
}

// 版本號 + 部署蓋章對所有人可見；各版本包含的 commits 只給 super_admin 看
function VersionPanel() {
    const { t, i18n } = useTranslation('system');
    // 大版本的多語系名稱（release-notes.json 的 name:{locale:text}）— 當前語系優先，fallback zh-TW → 任一
    const relName = (r) => r && r.name
        ? (r.name[i18n.language] || r.name['zh-TW'] || Object.values(r.name)[0] || '')
        : '';
    const { accountRole } = useTenant();
    const [v, setV] = useState(null);
    const [openVer, setOpenVer] = useState(null);   // 展開中的版本號（單開；預設最新版）
    useEffect(() => {
        let alive = true;
        get('/api/sip-relay/version').then((r) => {
            if (!alive) return;
            setV(r);
            // 預設展開最新一版
            if (r?.releases?.length) setOpenVer(r.releases[0].version);
        }).catch(() => {});
        return () => { alive = false; };
    }, []);
    if (!v) return null;
    const isSuper = accountRole === 'super_admin';
    return (
        <Card className="mt-8">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
                <History size={18} className="text-accent-700" strokeWidth={1.6} />
                <h2 className="heading-display text-lg font-semibold text-ink-900">{t('version.title')}</h2>
                <span className="ml-2 text-[11px] font-mono px-2 py-0.5 rounded border border-cream-300 text-ink-700 bg-white">
                    v{v.version}{v.sha ? ` · ${v.sha}` : ''}
                </span>
                {relName((v.releases || []).find((r) => r.version === v.version)) && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-accent-50 text-accent-700 border border-accent-300/50">
                        {relName((v.releases || []).find((r) => r.version === v.version))}
                    </span>
                )}
                {isSuper && v.dirty && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded border border-danger-500/40 text-danger-500 bg-danger-100/40">
                        {t('version.dirtyBadge')}
                    </span>
                )}
                {v.staging && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded border border-cream-300 text-ink-500 bg-cream-100">
                        staging
                    </span>
                )}
            </div>
            <p className="text-xs text-ink-500 mb-1">
                {v.deployedAt
                    ? t('version.deployedAt', { time: new Date(v.deployedAt).toLocaleString() })
                    : t('version.noBuildInfo')}
            </p>
            {isSuper && <ReleaseNotesExport />}
            {(v.releases || []).length > 0 && (
                <div className="mt-4 space-y-2">
                    {/* 只顯示最近 6 個版本，避免版本累積後列表無限長（舊版仍在 release-notes.json / git tag）*/}
                    {v.releases.slice(0, 6).map((r) => {
                        const expanded = openVer === r.version;
                        const count = (r.changes || []).length;
                        const isCurrent = r.version === v.version;
                        return (
                            <div key={r.version} className="rounded-lg border border-cream-300 bg-white overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setOpenVer(expanded ? null : r.version)}
                                    aria-expanded={expanded}
                                    className="w-full flex items-center gap-2 px-4 py-3 text-left transition hover:bg-cream-50"
                                >
                                    <ChevronRight
                                        size={15}
                                        className={`shrink-0 text-ink-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
                                    />
                                    <span className="font-mono text-sm font-semibold text-ink-900">v{r.version}</span>
                                    {isCurrent && (
                                        <span className="text-[9px] font-mono uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-success-100/60 text-success-500 border border-success-500/30">
                                            {t('version.currentBadge')}
                                        </span>
                                    )}
                                    <span className="text-[11px] text-ink-400">{r.date}</span>
                                    {relName(r) && (
                                        <span className="shrink-0 text-[11px] font-medium px-1.5 py-0.5 rounded bg-accent-50 text-accent-700 border border-accent-300/50">
                                            {relName(r)}
                                        </span>
                                    )}
                                    {r.summary && <span className="text-xs text-ink-500 truncate">{r.summary}</span>}
                                    <span className="ml-auto shrink-0 text-[11px] text-ink-400">
                                        {t('version.changeCount', { count })}
                                    </span>
                                </button>
                                {expanded && count > 0 && (
                                    <ul className="px-4 pb-3 pt-1 space-y-1 border-t border-cream-100 bg-cream-50/40">
                                        {r.changes.map((c) => (
                                            <li key={c.sha} className="flex items-start gap-2 text-xs text-ink-700 pt-1.5">
                                                <span className="font-mono text-[10px] text-ink-400 mt-0.5 shrink-0">{c.sha}</span>
                                                <span>{c.subject}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}

// ── PBX / 分機註冊 per tenant（sidecar /admin/warnings，30s 更新）──────────────
function useWarningsData() {
    const [w, setW] = useState(null);
    useEffect(() => {
        let alive = true;
        const load = () => warningsApi.fetch().then((r) => { if (alive) setW(r); }).catch(() => {});
        load();
        const t = setInterval(load, 30000);
        return () => { alive = false; clearInterval(t); };
    }, []);
    return w;
}

function PbxStatusPanel() {
    const { t } = useTranslation('system');
    const { tenants, isSuperAdmin } = useTenant();
    const w = useWarningsData();
    const visible = new Set((tenants || []).map((x) => x.hostId));
    const rows = (w?.tenants || []).filter((x) => visible.size === 0 || visible.has(x.hostId));
    return (
        <Card className="mt-8">
            <div className="flex items-center gap-2 mb-1">
                <Radio size={18} className="text-accent-700" strokeWidth={1.6} />
                <h2 className="heading-display text-lg font-semibold text-ink-900">{t('monitor.pbxTitle')}</h2>
                {isSuperAdmin && w?.ami && (
                    <span className={`ml-2 text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded border ${w.ami.connected ? 'border-success-500/40 text-success-500 bg-success-100' : 'border-danger-500/40 text-danger-500 bg-danger-100/40'}`}>
                        AMI {w.ami.connected ? t('warnings.amiConnected') : t('warnings.amiDown')}
                    </span>
                )}
            </div>
            <p className="text-xs text-ink-500 mb-4">{t('monitor.pbxDesc')}</p>
            {rows.length === 0 ? (
                <div className="text-xs text-ink-400 italic">{t('warnings.noTenantData')}</div>
            ) : (
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
                    {rows.map((x) => {
                        const reg = x.registrations || {};
                        const q = x.qualify || {};
                        const regBad = (reg.registered ?? 0) < (reg.total ?? 0);
                        const qBad = (q.unreachable ?? 0) > 0;
                        const bad = regBad || qBad;
                        return (
                            <div key={x.hostId} className={`rounded-lg border px-4 py-3 ${bad ? 'border-danger-500/40 bg-danger-100/20' : 'border-cream-300 bg-white'}`}>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-ink-900">{x.hostId}</span>
                                    {bad
                                        ? <AlertTriangle size={14} className="text-danger-500" />
                                        : <CheckCircle2 size={14} className="text-success-500" />}
                                </div>
                                <div className="mt-3 flex items-baseline gap-6">
                                    <div>
                                        <div className={`font-mono text-xl leading-none [font-variant-numeric:tabular-nums] ${regBad ? 'text-danger-500' : 'text-ink-900'}`}>
                                            {reg.registered ?? '—'}<span className="text-[13px] text-ink-400">/{reg.total ?? '—'}</span>
                                        </div>
                                        <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400">{t('warnings.registered')}</div>
                                    </div>
                                    <div>
                                        <div className={`font-mono text-xl leading-none [font-variant-numeric:tabular-nums] ${qBad ? 'text-danger-500' : 'text-ink-900'}`}>
                                            {q.reachable ?? '—'}<span className="text-[13px] text-ink-400">/{q.total ?? '—'}</span>
                                        </div>
                                        <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400">{t('warnings.pbxReachable')}</div>
                                    </div>
                                </div>
                                {(reg.issues || []).length > 0 && (
                                    <div className="mt-2.5 pt-2 border-t border-danger-500/20 space-y-1">
                                        {(reg.issues || []).slice(0, 3).map((iss, i) => (
                                            <div key={i} className="flex items-center gap-1.5 text-[11px] text-danger-500 truncate">
                                                <span className="size-1 rounded-full bg-danger-500 shrink-0" />
                                                {typeof iss === 'string' ? iss : (iss.ext || JSON.stringify(iss))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}

// ── 進行中通話（backend /api/sip-relay/live-calls，5s 更新）───────────────────
function LiveCallsPanel() {
    const { t } = useTranslation('system');
    const { tenants } = useTenant();
    const [data, setData] = useState(null);
    useEffect(() => {
        let alive = true;
        const load = () => get('/api/sip-relay/live-calls').then((r) => { if (alive) setData(r); }).catch(() => {});
        load();
        const tm = setInterval(load, 5000);
        return () => { alive = false; clearInterval(tm); };
    }, []);
    const visible = new Set((tenants || []).map((x) => x.hostId));
    const calls = (data?.calls || []).filter((c) => !c.hostId || visible.size === 0 || visible.has(c.hostId));
    return (
        <Card className="mt-6">
            <div className="flex items-center gap-2 mb-1">
                <PhoneCall size={18} className="text-accent-700" strokeWidth={1.6} />
                <h2 className="heading-display text-lg font-semibold text-ink-900">{t('monitor.liveCalls')}</h2>
                <span className="ml-1 text-xs font-mono text-ink-500">×{calls.length}</span>
            </div>
            <p className="text-xs text-ink-500 mb-3">{t('monitor.liveCallsDesc')}</p>
            {calls.length === 0 ? (
                <div className="text-xs text-ink-400 italic">{t('monitor.noCalls')}</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.15em] text-ink-400 border-b border-cream-200">
                                <th className="py-1.5 pr-3">{t('monitor.col.direction')}</th>
                                <th className="py-1.5 pr-3">{t('monitor.col.tenant')}</th>
                                <th className="py-1.5 pr-3">{t('monitor.col.avatar')}</th>
                                <th className="py-1.5 pr-3">{t('monitor.col.caller')}</th>
                                <th className="py-1.5 pr-3">{t('monitor.col.duration')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {calls.map((c) => (
                                <tr key={c.sessionId} className="border-b border-cream-100 last:border-0">
                                    <td className="py-1.5 pr-3">
                                        {c.direction === 'outbound'
                                            ? <span className="inline-flex items-center gap-1 text-accent-700"><PhoneOutgoing size={12} />{t('monitor.dir.outbound')}</span>
                                            : <span className="inline-flex items-center gap-1 text-success-500"><PhoneIncoming size={12} />{t('monitor.dir.inbound')}</span>}
                                    </td>
                                    <td className="py-1.5 pr-3 font-mono">{c.hostId || '—'}</td>
                                    <td className="py-1.5 pr-3 font-mono text-ink-500">{(c.avatarId || '').slice(0, 8)}</td>
                                    <td className="py-1.5 pr-3 font-mono [font-variant-numeric:tabular-nums]">{c.caller || '—'}</td>
                                    <td className="py-1.5 pr-3 font-mono [font-variant-numeric:tabular-nums]">{c.startedAt ? formatDur(Date.now() - c.startedAt) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}

function formatDur(ms) {
    const s = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ── 近期事件：哨兵/AI 巡檢事件流（Firestore systemEvents，含處理中/已恢復狀態）
//    + sidecar 事件環（AMI 斷線等）合併，新→舊。事件流的意義=意外事件要在這裡看得到。
function RecentFailuresPanel() {
    const { t } = useTranslation('system');
    const { tenants, isSuperAdmin } = useTenant();
    const w = useWarningsData();
    const [sysEvents, setSysEvents] = useState([]);
    useEffect(() => {
        let alive = true;
        const load = () => get('/api/sip-relay/system-events?limit=20')
            .then((r) => { if (alive) setSysEvents(r.events || []); }).catch(() => {});
        load();
        const tm = setInterval(load, 30000);
        return () => { alive = false; clearInterval(tm); };
    }, []);
    // tenant 級 sidecar 事件只給看得到該 tenant 的人；哨兵/巡檢事件由「後端」
    // /system-events 按呼叫者身分過濾（detail 點名別家 tenant 的整筆隱藏），這裡直接渲染
    const visible = new Set((tenants || []).map((x) => x.hostId));
    // 沒有 hostId 的是機房層事件（AMI/磁碟…）→ 只給超管；其餘照可見 tenant 過濾。
    const sidecarItems = (w?.recentFailures || [])
        .filter((f) => (f.hostId ? (visible.size === 0 || visible.has(f.hostId)) : isSuperAdmin))
        .map((f) => ({
            // ⚠️ at 是 ISO 字串,下面的 cutoff 比較是數字 —— 不轉成 ms 的話
            //    `'2026-08-04T…' >= 1754…` 會是 NaN 比較,恆 false,整批事件被靜默濾掉。
            key: `sc-${f.at}-${f.kind}`, at: Date.parse(f.at) || 0, active: false, severity: f.severity,
            title: t(`warnings.kind.${camel(f.kind)}`, { defaultValue: f.kind }),
            detail: `${f.hostId ? f.hostId + ' — ' : ''}${f.message || ''}`,
        }));
    const flowItems = sysEvents.map((e) => ({
        key: `ev-${e.id}`, at: e.lastAt, active: e.status === 'active', severity: e.severity,
        title: `[${e.region || '-'}] ${e.type || e.code}`,
        detail: e.detail || '', cause: e.cause || '',
        durationMin: Math.max(1, Math.round(((e.status === 'active' ? Date.now() : (e.resolvedAt || e.lastAt)) - (e.firstAt || e.lastAt)) / 60000)),
        count: e.count || 1,
    }));
    // 顯示規則：未恢復（active）一律顯示；已恢復只留 24 小時內的（舊事件不堆版面）
    const DAY_MS = 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - DAY_MS;
    const items = [...flowItems, ...sidecarItems]
        .filter((f) => f.active || (f.at || 0) >= cutoff)
        .sort((a, b) => (a.active === b.active ? (b.at || 0) - (a.at || 0) : (a.active ? -1 : 1)))
        .slice(0, 50);
    return (
        <Card className="mt-6">
            <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={18} className="text-warn-500" strokeWidth={1.6} />
                <h2 className="heading-display text-lg font-semibold text-ink-900">{t('warnings.recentEvents')}</h2>
            </div>
            {items.length === 0 ? (
                <div className="text-xs text-ink-400 italic mt-2">{t('monitor.noFailures')}</div>
            ) : (
                <div className="mt-2 space-y-2 max-h-80 overflow-y-auto overscroll-contain pr-1">
                    {items.map((f) => (
                        <div key={f.key} className="flex items-start gap-2.5 text-[12px]">
                            {f.active
                                ? <span className="mt-1 size-1.5 rounded-full shrink-0 bg-danger-500 animate-pulse" />
                                : <CheckCircle2 size={13} className="mt-0.5 text-success-500 shrink-0" strokeWidth={2} />}
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-ink-800">{f.title}</span>
                                    {f.active ? (
                                        <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-danger-500 px-1.5 py-px rounded border border-danger-500/40 bg-danger-100/40">
                                            {t('monitor.evActive')}{f.durationMin ? ` · ${t('monitor.evDurationMin', { n: f.durationMin })}` : ''}
                                        </span>
                                    ) : f.durationMin ? (
                                        <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-success-500 px-1.5 py-px rounded border border-success-500/30 bg-success-100">
                                            {t('monitor.evResolved')} · {t('monitor.evDurationMin', { n: f.durationMin })}
                                        </span>
                                    ) : null}
                                    <span className="font-mono text-[11px] text-ink-400">{f.at ? new Date(f.at).toLocaleString() : ''}</span>
                                </div>
                                {(f.detail || f.cause) && (
                                    <div className="text-ink-500 truncate mt-0.5">{f.detail}{f.cause ? ` ｜${f.cause}` : ''}</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

function camel(s) {
    return String(s || '').replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

// 即時記錄是整台 backend 的 log、不分租戶 → 只給超管；其他帳號只能下載自己租戶的封包
function DiagnosticToolsPanel() {
    const { t } = useTranslation('system');
    const { hostId, tenants, isSuperAdmin } = useTenant();
    const tenant = tenants.find((t) => t.hostId === hostId);
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [busy, setBusy] = useState(null);   // 'start' | 'stop' | 'download' | null
    const [error, setError] = useState(null);
    const [ttl, setTtl] = useState(30);
    const [period, setPeriod] = useState('24h');
    const [filter, setFilter] = useState('');
    const [autoScroll, setAutoScroll] = useState(true);
    const [downloadedAt, setDownloadedAt] = useState(null);
    const sinceSeqRef = useRef(0);
    const logsEndRef = useRef(null);

    async function loadStatus() {
        try {
            const r = await diagnosticApi.status();
            setStatus(r);
        } catch (e) {
            setError(e);
        }
    }

    async function pollLogs() {
        try {
            const r = await diagnosticApi.logs(sinceSeqRef.current, 500);
            if (r.lines?.length) {
                sinceSeqRef.current = r.lines[r.lines.length - 1].seq;
                setLogs((prev) => [...prev, ...r.lines].slice(-2000));
            }
            if (r.status) setStatus(r.status);
        } catch (e) {
            setError(e);
        }
    }

    useEffect(() => { if (isSuperAdmin) loadStatus(); }, [isSuperAdmin]);

    useEffect(() => {
        if (!status?.enabled) return;
        const t = setInterval(pollLogs, 2000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status?.enabled]);

    useEffect(() => {
        if (autoScroll && logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [logs, autoScroll]);

    async function startRecording() {
        setBusy('start');
        setError(null);
        try {
            const r = await diagnosticApi.toggle(true, ttl);
            setStatus(r);
            sinceSeqRef.current = 0;
            setLogs([]);
            pollLogs();
        } catch (e) {
            setError(e);
        } finally {
            setBusy(null);
        }
    }

    async function stopAndDownload() {
        setBusy('stop');
        setError(null);
        try {
            // 沒選租戶（超管從徽章進來）→ 沒有可下載的租戶 log，只關閉；否則 dump 失敗會連關閉都沒做
            if (hostId) await diagnosticApi.dumpTenant(hostId, period);
            const r = await diagnosticApi.toggle(false);
            setStatus(r);
            setDownloadedAt(Date.now());
        } catch (e) {
            setError(e);
        } finally {
            setBusy(null);
        }
    }

    async function downloadOnly() {
        if (!hostId) return;
        setBusy('download');
        setError(null);
        try {
            await diagnosticApi.dumpTenant(hostId, period);
            setDownloadedAt(Date.now());
        } catch (e) {
            setError(e);
        } finally {
            setBusy(null);
        }
    }

    const enabled = !!status?.enabled;
    const remainingMin = enabled ? Math.ceil((status.remainingMs || 0) / 60000) : 0;
    const filtered = filter
        ? logs.filter((l) => l.msg?.toLowerCase().includes(filter.toLowerCase()))
        : logs;

    return (
        <Card className="mt-8">
            <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-md ${enabled ? 'bg-warn-100' : 'bg-accent-50'}`}>
                    <LifeBuoy size={20} className={enabled ? 'text-warn-500' : 'text-accent-700'} strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <h2 className="heading-display text-lg font-semibold text-ink-900">
                            {t('diagnostic.title')}
                        </h2>
                        {enabled && (
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-warn-500 px-2 py-0.5 rounded border border-warn-500/40 bg-warn-100">
                                <span className="size-1.5 rounded-full bg-warn-500 animate-pulse" />
                                {t('diagnostic.recordingRemaining', { n: remainingMin })}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-ink-500 leading-relaxed">
                        {t(isSuperAdmin ? 'diagnostic.howto' : 'diagnostic.howtoDownload')}
                    </p>

                    {/* 控制列 */}
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">
                            {t('diagnostic.tenant')}<span className="text-ink-700 normal-case">{tenant?.displayName || hostId || '—'}</span>
                        </span>
                        <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">{t('diagnostic.dumpPeriod')}</span>
                            <select
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-md border border-cream-200 bg-white"
                                disabled={!!busy}
                            >
                                <option value="1h">{t('diagnostic.period.1h')}</option>
                                <option value="6h">{t('diagnostic.period.6h')}</option>
                                <option value="24h">{t('diagnostic.period.24h')}</option>
                                <option value="7d">{t('diagnostic.period.7d')}</option>
                            </select>
                        </div>
                        {isSuperAdmin && !enabled && (
                            <div className="flex items-center gap-1.5">
                                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">{t('diagnostic.recordDuration')}</span>
                                <select
                                    value={ttl}
                                    onChange={(e) => setTtl(Number(e.target.value))}
                                    className="text-xs px-2 py-1.5 rounded-md border border-cream-200 bg-white"
                                    disabled={!!busy}
                                >
                                    <option value={10}>{t('diagnostic.ttl.10')}</option>
                                    <option value={30}>{t('diagnostic.ttl.30')}</option>
                                    <option value={60}>{t('diagnostic.ttl.60')}</option>
                                    <option value={120}>{t('diagnostic.ttl.120')}</option>
                                </select>
                            </div>
                        )}

                        <div className="flex-1" />

                        {isSuperAdmin && !enabled && (
                            <Button variant="accent" disabled={!!busy} onClick={startRecording}>
                                <Circle size={10} className="fill-current" /> {busy === 'start' ? t('diagnostic.starting') : t('diagnostic.startRecording')}
                            </Button>
                        )}
                        {enabled && (
                            <Button variant="accent" disabled={!!busy} onClick={stopAndDownload}>
                                <Square size={12} className="fill-current" /> {busy === 'stop' ? t('diagnostic.processing') : t('diagnostic.stopAndDownload')}
                            </Button>
                        )}
                        <Button variant="ghost" disabled={!!busy || !hostId} onClick={downloadOnly}>
                            <Download size={14} /> {busy === 'download' ? t('diagnostic.packaging') : t('diagnostic.downloadOnly')}
                        </Button>
                    </div>

                    {downloadedAt && !error && !busy && (
                        <div className="mt-2 text-xs text-success-500">{t('diagnostic.downloaded')}</div>
                    )}

                    {error && (
                        <div className="mt-3 px-3 py-2 bg-danger-100/40 border border-danger-500/30 rounded text-xs text-danger-500">
                            {error.message}
                        </div>
                    )}

                    {/* Live tail：記錄中才顯示 */}
                    {enabled && (
                        <div className="mt-5">
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                                <input
                                    type="text"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder={t('diagnostic.searchPlaceholder')}
                                    className="flex-1 min-w-[200px] text-xs px-2.5 py-1.5 rounded-md border border-cream-200 bg-white"
                                />
                                <label className="flex items-center gap-1.5 text-xs text-ink-500">
                                    <input
                                        type="checkbox"
                                        checked={autoScroll}
                                        onChange={(e) => setAutoScroll(e.target.checked)}
                                    />
                                    {t('diagnostic.autoScroll')}
                                </label>
                                <Button size="sm" variant="ghost" onClick={() => { setLogs([]); sinceSeqRef.current = 0; }}>
                                    <Trash2 size={12} /> {t('diagnostic.clear')}
                                </Button>
                                <span className="font-mono text-[10px] text-ink-400 tabular-nums">
                                    {t('diagnostic.logCount', { shown: filtered.length, total: logs.length })}
                                </span>
                            </div>

                            <div className="bg-[#1c1a17] text-[#d8d3cc] rounded-md font-mono text-[11px] leading-relaxed h-96 overflow-auto p-3 border border-cream-300">
                                {filtered.length === 0 ? (
                                    <div className="text-ink-400 italic">
                                        {filter ? t('diagnostic.noMatch', { filter }) : t('diagnostic.waiting')}
                                    </div>
                                ) : (
                                    filtered.map((line) => <LogLine key={line.seq} line={line} />)
                                )}
                                <div ref={logsEndRef} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}

function LogLine({ line }) {
    const tone = line.level === 'error' ? 'text-[#f08080]'
        : line.level === 'warn' ? 'text-[#e6c07b]'
        : line.level === 'info' ? 'text-[#9ec5e6]'
        : 'text-[#d8d3cc]';
    const ts = new Date(line.t).toISOString().slice(11, 23);
    return (
        <div className="whitespace-pre-wrap break-words">
            <span className="text-[#7a7268]">{ts}</span>{' '}
            <span className={`${tone} font-semibold`}>[{line.level}]</span>{' '}
            <span>{line.msg}</span>
        </div>
    );
}

function computeOverall(data, t) {
    if (!data) return { kind: 'loading', label: t('health.overall.checking') };
    const allUp = data.backend?.reachable && data.sidecar?.reachable && data.ivrBuilder?.reachable;
    if (allUp) return { kind: 'ok', label: t('health.overall.ok') };
    return { kind: 'fail', label: t('health.overall.partialDown') };
}

function buildAdminConsoleState(data, error) {
    if (error) return { reachable: false, error: error.message, latencyMs: null };
    if (!data) return null;
    return { reachable: true, status: 200, latencyMs: 0, url: window.location.origin };
}

function OverallBanner({ overall, lastFetched, loading, error }) {
    const { t } = useTranslation('system');
    const bg = overall.kind === 'ok'
        ? 'bg-success-100 border-success-500/30 text-success-500'
        : overall.kind === 'fail'
        ? 'bg-danger-100/40 border-danger-500/30 text-danger-500'
        : 'bg-cream-100 border-cream-300 text-ink-500';
    const Icon = overall.kind === 'ok' ? CheckCircle2 : overall.kind === 'fail' ? AlertTriangle : Activity;
    return (
        <Card className={`flex items-center gap-3 ${bg}`} padded={false}>
            <div className="flex items-center gap-3 px-5 py-4">
                <Icon size={20} strokeWidth={1.7} />
                <div>
                    <div className="text-sm font-semibold leading-tight">{overall.label}</div>
                    <div className="text-[11px] opacity-70 mt-0.5">
                        {loading && !lastFetched ? t('common:state.loading') : lastFetched
                            ? t('health.updatedAgo', { time: formatRelative(lastFetched, t) })
                            : '—'}
                        {error && ` · ${error.message}`}
                    </div>
                </div>
            </div>
        </Card>
    );
}

function ServiceCard({ meta, state }) {
    const { t } = useTranslation('system');
    const Icon = meta.icon;
    const serviceLabel = t(`health.service.${meta.key}.label`);
    const serviceHint = t(`health.service.${meta.key}.hint`);
    if (!state) {
        return (
            <Card>
                <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-cream-100">
                        <Icon size={18} className="text-ink-400" strokeWidth={1.6} />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-ink-900">{serviceLabel}</div>
                        <div className="text-xs text-ink-400 mt-0.5">{t('health.overall.checking')}</div>
                    </div>
                </div>
            </Card>
        );
    }
    const ok = state.reachable && (state.status == null || state.status < 500);
    const dot = ok ? 'bg-success-500' : 'bg-danger-500';
    const ring = ok ? 'ring-success-500/20' : 'ring-danger-500/20';
    const latencyTone = state.latencyMs == null ? 'text-ink-400'
        : state.latencyMs > 500 ? 'text-warn-500'
        : 'text-ink-700';
    return (
        <Card className={`relative transition ring-1 ${ring}`}>
            <div className="flex items-start gap-3">
                <div className={`p-2 rounded-md ${ok ? 'bg-success-100' : 'bg-danger-100/60'}`}>
                    <Icon size={18} className={ok ? 'text-success-500' : 'text-danger-500'} strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-ink-900">{serviceLabel}</span>
                        <span className={`inline-block size-2 rounded-full ${dot} ${ok ? 'animate-pulse' : ''}`} />
                    </div>
                    <div className="text-xs text-ink-400 mt-0.5">{serviceHint}</div>
                </div>
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                <Stat label={t('health.stat.status')} value={
                    <span className={ok ? 'text-success-500 font-medium' : 'text-danger-500 font-medium'}>
                        {ok ? 'UP' : 'DOWN'}
                    </span>
                } />
                <Stat label={t('health.stat.http')} mono value={state.status ?? '—'} />
                <Stat label={t('health.stat.latency')} mono value={
                    <span className={latencyTone}>
                        {state.latencyMs != null ? `${state.latencyMs}ms` : '—'}
                    </span>
                } />
                <Stat label={t('health.stat.checkedAt')} mono value={
                    <span className="text-[10px] text-ink-500">
                        {new Date().toLocaleTimeString('zh-TW', { hour12: false })}
                    </span>
                } />
            </dl>

            {!ok && state.error && (
                <div className="mt-3 px-2.5 py-1.5 bg-danger-100/40 rounded text-[11px] text-danger-500 font-mono break-all">
                    {state.error}
                </div>
            )}
        </Card>
    );
}

function Stat({ label, value, mono = false }) {
    return (
        <div>
            <dt className="font-mono text-[9px] uppercase tracking-[0.18em] text-ink-400">{label}</dt>
            <dd className={`text-sm mt-0.5 ${mono ? 'tnum font-mono' : ''}`}>{value}</dd>
        </div>
    );
}

function formatRelative(ts, t) {
    const diff = Math.max(0, (Date.now() - ts) / 1000);
    if (diff < 2) return t('health.justNow');
    if (diff < 60) return t('health.secondsAgo', { n: Math.floor(diff) });
    return t('health.minutesAgo', { n: Math.floor(diff / 60) });
}
